import { Component, ElementRef, Input, ViewChild, SimpleChanges, OnChanges, OnDestroy, Optional, Output, EventEmitter, Self } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NgControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatFormFieldControl } from '@angular/material/form-field';
import { throwError, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';

import { getContainerProjectId, getRoleMatrix } from '../../container.utils';
import { ContainerAccess } from '../../enums/container-access.enum';
import { PermissionAction } from '../../enums/permission-action.enum';
import { CoreApiService } from '../../../core-api.service';
import { LoggingService } from '../../../logging.service';
import { Container } from '../../models/container.model';
import { File } from '../../models/file.model';
import { NotificationService } from '../../../notification.service';
import { ProfileService } from '../../../profile/profile.service';
import { ProjectsService } from '../../../projects/projects.service';
import { TranslationBundle, TranslationService } from '../../../translation.service';

@Component({
  host: {
    'class': 'fw-tags',
    '[class.disabled]': '!writable',
    'test-id': 'fw-tags',
  },
  selector: 'fw-tags',
  styleUrls: ['./fw-tags.component.scss'],
  templateUrl: './fw-tags.component.html',
  providers: [{
    provide: MatFormFieldControl,
    useExisting: FwTagsComponent,
  }],
})
export class FwTagsComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  @Input() allowCustomTags = false
  @Input() autoSave = true
  @Input() container: Container
  @Input() displayActionMenu = true
  @Input() file: File
  /** Check also 'fw-multi-selector-ext' component in the inline case */
  @Input() useAsInlineField = false
  @Input() writable = true

  @Output() afterSave = new EventEmitter<string[]>()
  @Output() change = new EventEmitter<string[]>()

  @ViewChild('autoComplete', { static: false }) autoComplete: MatAutocomplete
  @ViewChild('chipInput', { read: MatAutocompleteTrigger }) autoCompleteTrigger: MatAutocompleteTrigger
  @ViewChild('chipInput', { static: false }) chipInput: ElementRef

  public form = new FormGroup({
    chips: new FormControl([]),
    text: new FormControl(),
  })
  public availableTags: string[]
  public suggestedTags: string[] = []
  public fetchingAvailableTags = false
  public hasTags = false
  public canModifyTags = false

  private messages = TranslationBundle.EMPTY
  private subscriptions = {
    getCachedGroup: Subscription.EMPTY,
    messages: Subscription.EMPTY,
  }

  constructor(
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
    private translate: TranslationService,
    @Self() @Optional() ngControl: NgControl,
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }

    this.subscriptions.messages = this.translate.getBundle('fwTags').subscribe(messages => {
      this.messages = messages;
    });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const containerChanged = changes.container && this.container?._id && (changes.container.firstChange || this.container._id !== changes.container.previousValue?._id);
    const fileChanged = changes.file && this.file?._id && (changes.file.firstChange || this.file._id !== changes.file.previousValue?._id);
    if (containerChanged || fileChanged) {
      this.updatePermissions();
    }
    const tags = this.container?.tags || this.file?.tags || [];
    this.form.controls.chips.setValue(tags);
    this.hasTags = tags.length > 0;
    const parentGroup = this.container?.group || this.container?.parents?.group || this.file?.parents.group;
    if (parentGroup && !this.availableTags && !this.fetchingAvailableTags) {
      this.fetchingAvailableTags= true;
      this.subscriptions.getCachedGroup.unsubscribe();
      this.subscriptions.getCachedGroup = this.projectsService.getCachedGroup(parentGroup).subscribe(
        group => {
          this.fetchingAvailableTags = false;
          this.availableTags = group.tags || [];
        },
        error => {
          this.logger.error(error);
        },
      );
    }
  }

  filterAutocomplete(): void {
    if (!this.availableTags) {
      return;
    }
    // push off the stack to run after current change detection cycle
    setTimeout(() => {
      const value = this.form.controls.text.value;
      // case insensitive filter
      this.suggestedTags = this.availableTags.filter(tag =>
        this.form.controls.chips.value.findIndex(v => v.toLowerCase() === tag.toLowerCase()) < 0 &&
        (!value || tag.toLowerCase().includes(value.toLowerCase()))
      );
      if (this.suggestedTags.length > 0) {
        this.autoCompleteTrigger.openPanel();
      }
    });
  }

  clearChipInput(): void {
    this.chipInput.nativeElement.value = '';
    this.form.controls.text.reset();
  }

  isValidTag(value: string): boolean {
    return !this.form.controls.chips.value.includes(value) && (this.allowCustomTags || (this.availableTags || []).includes(value));
  }

  addToList(value: string): void {
    const { chips } = this.form.controls;
    chips.setValue([...chips.value, value]);
    this.hasTags = true;
    this.change.emit(this.form.controls.chips.value);
    this.onChange(this.form.controls.chips.value);
  }

  removeFromList(tabIndex: number): void {
    const { chips } = this.form.controls;
    chips.setValue([
      ...chips.value.slice(0, tabIndex),
      ...chips.value.slice(tabIndex + 1),
    ]);
    this.hasTags = Boolean(chips.value.length);
    this.change.emit(this.form.controls.chips.value);
    this.onChange(this.form.controls.chips.value);
  }

  addTag(event: MatChipInputEvent | MatAutocompleteSelectedEvent): void {
    const value = (event as MatChipInputEvent).value || (event as MatAutocompleteSelectedEvent).option?.value;
    if (!value || !this.isValidTag(value)) {
      return;
    }
    setTimeout(() => {
      // defer this to drop the partial value added by the input blur event
      this.clearChipInput();
      if (this.autoSave) {
        (this.container
          ? this.coreApi.containers.tags.post(this.container._id, { value: value })
          : this.file
            ? this.coreApi.files.tags.put(this.file.file_id, [value, ...this.file.tags])
            : throwError(new Error('Missing container or file.'))
        ).subscribe(
          () => {
            this.addToList(value);
            this.afterSave.emit(this.form.controls.chips.value);
          },
          error => {
            this.logger.error(error);
            this.notifications.error('tag.add.error');
          },
        );
      } else {
        this.addToList(value);
      }
    });
  }

  removeTag(tabIndex: number): void {
    const { chips } = this.form.controls;
    const value = chips.value[tabIndex];
    if (this.autoSave) {
      (this.container
        ? this.coreApi.containers.tags.delete(this.container._id, value)
        : this.file
          ? this.coreApi.files.tags.put(this.file.file_id, this.file.tags.filter(tag => tag !== value))
          : null
      ).subscribe(
        () => {
          this.removeFromList(tabIndex);
          this.afterSave.emit(this.form.controls.chips.value);
        },
        error => {
          this.logger.error(error);
          this.notifications.error('tag.delete.error');
        },
      );
    } else {
      this.removeFromList(tabIndex);
    }
  }

  private updatePermissions(): void {
    const projectId = this.container ? getContainerProjectId(this.container) : this.file?.parents.project;
    if (projectId) {
      this.projectsService.getProjectPermissions(projectId).subscribe(projectPermissions => {
        this.canModifyTags = this.writable && projectPermissions.includes(PermissionAction.TagsManage);
      });
    } else {
      // Fall back to roleMatrix if the container doesn't have roles
      this.profileService.getProfile().pipe(first()).subscribe(profile => {
        const roleMatrix = getRoleMatrix(this.container, profile);
        this.canModifyTags = this.writable && roleMatrix[ContainerAccess.ReadWrite];
      });
    }
  }

  /** ControlValueAccessor */
  onChange: Function = () => { } // eslint-disable-line @typescript-eslint/no-empty-function
  onTouched: Function = () => { } // eslint-disable-line @typescript-eslint/no-empty-function

  writeValue(value: string[] = []): void {
    const { chips } = this.form.controls;
    chips.setValue(value);
    this.hasTags = Boolean(chips?.value?.length);
    this.change.emit(this.form.controls.chips.value);
  }

  registerOnChange(fn: Function): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }
}
